import React from 'react';
import { motion } from 'framer-motion';
import { FaPhone, FaDesktop, FaCheckCircle, FaWhatsapp, FaEnvelope, FaTools } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styles from './TIPresencial.module.css';
import PageSEO from '../../components/PageSEO/PageSEO';

const WA_LINK = 'https://wa.me/5548988393447?text=Ol%C3%A1%2C%20gostaria%20de%20saber%20mais%20sobre%20o%20servi%C3%A7o%20de%20T.I%20Presencial%20da%20SteinSoft.';

const reveal = (delay = 0) => ({
  initial:     { opacity: 0, y: 32 },
  whileInView: { opacity: 1, y: 0  },
  viewport:    { once: true        },
  transition:  { duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] },
});

export default function TIPresencial() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const lang = i18n.language?.slice(0, 2) || 'pt';

  const telefonia = [
    t('tiPresencial.t1'),
    t('tiPresencial.t2'),
    t('tiPresencial.t3'),
    t('tiPresencial.t4'),
  ];

  const computadores = [
    t('tiPresencial.c1'),
    t('tiPresencial.c2'),
    t('tiPresencial.c3'),
    t('tiPresencial.c4'),
  ];

  return (
    <div className={styles.page}>
      <PageSEO
        title="T.I Presencial — Suporte em Telefonia e Computadores"
        description="A SteinSoft vai até você: configuração de PABX, VoIP, softphones, manutenção de computadores, instalação de Windows e impressoras. Atendimento presencial."
        path="/TIPresencial"
        lang={lang}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'Service',
          name: 'T.I Presencial — SteinSoft',
          description: 'Serviço de suporte presencial em telefonia (PABX, VoIP) e computadores (manutenção, formatação, impressoras).',
          provider: { '@type': 'Organization', name: 'SteinSoft', url: 'https://steinsoft.com.br' },
          url: 'https://steinsoft.com.br/TIPresencial',
        }}
      />

      {/* ── HERO ──────────────────────────────────────────────── */}
      <section className={styles.hero}>
        <div className={styles.heroBg} aria-hidden />
        <div className={styles.heroInner}>
          <motion.div className={styles.eyebrow} {...reveal(0)}>
            <span className={styles.eyebrowDot} />
            {t('tiPresencial.eyebrow')}
          </motion.div>

          <motion.h1 className={styles.heroTitle} {...reveal(0.1)}>
            {t('tiPresencial.titulo')}{' '}
            <span className={styles.accent}>{t('tiPresencial.tituloAcc')}</span>
          </motion.h1>

          <motion.p className={styles.heroSub} {...reveal(0.2)}>
            {t('tiPresencial.subtitulo')}
          </motion.p>

          <motion.div className={styles.heroActions} {...reveal(0.3)}>
            <a href={WA_LINK} target="_blank" rel="noopener noreferrer" className={styles.btnPrimary}>
              <FaWhatsapp /> {t('tiPresencial.btnWhatsApp')}
            </a>
            <button className={styles.btnGhost} onClick={() => navigate('/Contato')}>
              <FaEnvelope /> {t('tiPresencial.btnEmail')}
            </button>
          </motion.div>
        </div>
      </section>

      {/* ── SERVIÇOS ──────────────────────────────────────────── */}
      <section className={styles.servicesSection}>
        <div className={styles.sectionInner}>
          <motion.div className={styles.sectionHeader} {...reveal()}>
            <h2 className={styles.sectionTitle}>
              O que a gente <span className={styles.accent}>resolve</span>
            </h2>
            <p className={styles.sectionSub}>
              Dois pilares de atendimento presencial para manter sua empresa funcionando.
            </p>
          </motion.div>

          <div className={styles.servicesGrid}>
            {/* Telefonia */}
            <motion.div className={styles.serviceCard} {...reveal(0.1)}>
              <div className={styles.cardHeader}>
                <div className={styles.cardIconWrap} data-color="amber">
                  <FaPhone />
                </div>
                <div>
                  <h3 className={styles.cardTitle}>{t('tiPresencial.telefoniaTitle')}</h3>
                  <p className={styles.cardDesc}>{t('tiPresencial.telefoniaDesc')}</p>
                </div>
              </div>
              <ul className={styles.serviceList}>
                {telefonia.map((item, i) => (
                  <li key={i}>
                    <FaCheckCircle className={styles.listIcon} />
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Computadores */}
            <motion.div className={styles.serviceCard} {...reveal(0.2)}>
              <div className={styles.cardHeader}>
                <div className={styles.cardIconWrap} data-color="blue">
                  <FaDesktop />
                </div>
                <div>
                  <h3 className={styles.cardTitle}>{t('tiPresencial.computadoresTitle')}</h3>
                  <p className={styles.cardDesc}>{t('tiPresencial.computadoresDesc')}</p>
                </div>
              </div>
              <ul className={styles.serviceList}>
                {computadores.map((item, i) => (
                  <li key={i}>
                    <FaCheckCircle className={styles.listIcon} />
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── CTA FINAL ─────────────────────────────────────────── */}
      <section className={styles.ctaSection}>
        <div className={styles.ctaGlow} aria-hidden />
        <motion.div className={styles.ctaInner} {...reveal()}>
          <div className={styles.ctaIconWrap}>
            <FaTools />
          </div>
          <h2 className={styles.ctaTitle}>
            {t('tiPresencial.ctaTitulo')}
          </h2>
          <p className={styles.ctaDesc}>
            {t('tiPresencial.ctaDesc')}
          </p>
          <div className={styles.ctaActions}>
            <a href={WA_LINK} target="_blank" rel="noopener noreferrer" className={styles.btnPrimary}>
              <FaWhatsapp /> {t('tiPresencial.ctaWhatsApp')}
            </a>
            <button className={styles.btnGhost} onClick={() => navigate('/Contato')}>
              <FaEnvelope /> {t('tiPresencial.ctaEmail')}
            </button>
          </div>
        </motion.div>
      </section>
    </div>
  );
}
